'use client';

import { useRewardsLeaderboard } from '@/lib/api/rewards-leaderboard/hooks';
import type { LeaderboardEntry, RewardsLeaderboard } from '@/lib/api/rewards-leaderboard/models';
import {
  type BoardSegment,
  LEADERBOARD_BOARD,
  LEADERBOARD_TOP_COUNT,
  OTHER_SEGMENT,
} from './constants';

interface BoardRead {
  data?: RewardsLeaderboard;
  isLoading: boolean;
}

export interface BoardState extends BoardRead {
  /** The top three, best first; empty while the board loads. */
  leaders: LeaderboardEntry[];
}

function toBoardState({ data, isLoading }: BoardRead): BoardState {
  const leaders = (data?.entries ?? []).filter((entry) => entry.position <= LEADERBOARD_TOP_COUNT);
  return { data, isLoading, leaders };
}

/** The default board, unless it came back empty: then the other one. */
function pickOpeningSegment(boards: Record<BoardSegment, BoardState>): BoardSegment {
  const preferred = LEADERBOARD_BOARD.segment;
  const { isLoading, leaders } = boards[preferred];
  if (isLoading || leaders.length > 0) return preferred;
  return OTHER_SEGMENT[preferred];
}

/**
 * Both boards of the Leaderboard card at once. A new month starts every board
 * from zero, and the prediction one can stay empty for days, so the card opens
 * on whichever has leaders instead of vanishing.
 */
export function useLeaderboardBoards() {
  const { data: predictionBoard, isLoading: isPredictionLoading } = useRewardsLeaderboard({
    ...LEADERBOARD_BOARD,
    segment: 'prediction',
  });
  const { data: cryptoBoard, isLoading: isCryptoLoading } = useRewardsLeaderboard({
    ...LEADERBOARD_BOARD,
    segment: 'crypto',
  });

  const boards: Record<BoardSegment, BoardState> = {
    prediction: toBoardState({ data: predictionBoard, isLoading: isPredictionLoading }),
    crypto: toBoardState({ data: cryptoBoard, isLoading: isCryptoLoading }),
  };
  return { boards, openingSegment: pickOpeningSegment(boards) };
}
